import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { FeatureOverridesModal } from '~/components/FeatureOverridesModal';
import { Link } from '~/components/Link';
import useCookieStorage from '~/hooks/useCookieStorage';
import { useUser } from '~/hooks/useUser';
import { EXTENSION_INSTALLED_COOKIE_NAME } from '~/scripts/constants';
import { isAdmin } from '~/scripts/isAdmin';
import { adminMenuItems } from './AdminMenu';
import { customCrawlsItem, productMenuGroups } from './ProductMenu';
import { profileMenuItems } from './ProfileMenu';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  queryParams: string;
}

interface MobileLink {
  title: string;
  href: string;
}

export const MobileMenu = ({ open, onClose, queryParams }: MobileMenuProps) => {
  const user = useUser();
  const [featureOverridesOpen, setFeatureOverridesOpen] = useState(false);

  const onFeatureOverridesOpen = () => {
    onClose();
    setFeatureOverridesOpen(true);
  };

  const onFeatureOverridesClose = () => {
    setFeatureOverridesOpen(false);
  };

  const [extensionInstalled] = useCookieStorage(
    EXTENSION_INSTALLED_COOKIE_NAME,
    ''
  );

  const links: MobileLink[] = useMemo(() => {
    const items: MobileLink[] = [customCrawlsItem];
    if (user.isUpgraded !== true) {
      items.push({ title: 'Pricing', href: '/pricing' + queryParams });
    }
    items.push({ title: 'Help and support', href: '/support' });
    if (!extensionInstalled) {
      items.push({ title: 'Free extension', href: '/extension' });
    }
    if (user.isGuest) {
      items.push({ title: 'Log in', href: '/login' + queryParams });
    } else {
      items.push(...profileMenuItems);
    }
    return items;
  }, [extensionInstalled, queryParams, user.isGuest, user.isUpgraded]);

  return (
    <>
      <Collapse in={open} unmountOnExit sx={{ display: { md: 'none' } }}>
        <Paper
          square
          elevation={8}
          sx={{ maxHeight: 'calc(100dvh - 70px)', overflowY: 'auto' }}>
          {productMenuGroups.map((group) => {
            return (
              <Accordion
                key={group.title}
                disableGutters
                square
                elevation={0}
                defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 500 }}>
                    {group.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding dense>
                    {group.items.map((item) => {
                      return (
                        <ListItemButton
                          key={item.href}
                          component={Link}
                          href={item.href}
                          underline="none"
                          color="inherit"
                          onClick={onClose}
                          divider
                          sx={{ pl: 3 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <item.icon />
                          </ListItemIcon>
                          <ListItemText primary={item.title} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
          {isAdmin(user) && (
            <Accordion disableGutters square elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <ListItemIcon sx={{ minWidth: 40, alignItems: 'center' }}>
                  <AdminPanelSettings />
                </ListItemIcon>
                <Typography sx={{ fontWeight: 500 }}>Admin</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List disablePadding dense>
                  {adminMenuItems.map((item) => {
                    return (
                      <ListItemButton
                        key={item.href}
                        component={Link}
                        href={item.href}
                        underline="none"
                        color="inherit"
                        onClick={onClose}
                        divider
                        sx={{ pl: 3 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.title} />
                      </ListItemButton>
                    );
                  })}
                  <ListItemButton
                    onClick={onFeatureOverridesOpen}
                    divider
                    sx={{ pl: 3 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TuneIcon />
                    </ListItemIcon>
                    <ListItemText primary="Feature Overrides" />
                  </ListItemButton>
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          <Divider />
          <List disablePadding>
            {links.map((item) => {
              return (
                <ListItemButton
                  key={item.href}
                  component={Link}
                  href={item.href}
                  underline="none"
                  color="inherit"
                  onClick={onClose}
                  divider>
                  <ListItemText primary={item.title} />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      </Collapse>
      {isAdmin(user) && (
        <FeatureOverridesModal
          open={featureOverridesOpen}
          onClose={onFeatureOverridesClose}
        />
      )}
    </>
  );
};
